<template>
  <div class="ibb-sheet">
    <div class="ibb-sheet__topbar">
      <button class="ibb-sheet__back" @click="goBack" :title="$t('buttons.close') || 'Cerrar'">
        ←
      </button>
      <span class="ibb-sheet__filename">{{ filename }}</span>
      <span class="ibb-sheet__status" :class="statusClass">
        <span class="ibb-sheet__status-dot" v-if="statusIconKind"></span>
        {{ statusText }}
      </span>
      <div class="ibb-sheet__export-wrap" v-click-outside="closeExportMenu">
        <button
          class="ibb-sheet__history"
          @click="toggleExportMenu"
          :title="$t('files.exportMenu') || 'Exportar e imprimir'"
        >
          <span class="ibb-sheet__history-icon">⬇</span>
          <span class="ibb-sheet__history-text">{{ $t('files.exportMenu') || 'Exportar' }}</span>
        </button>
        <ul v-if="showExportMenu" class="ibb-sheet__export-menu">
          <li @click="exportDocx">{{ $t('files.downloadDocx') || 'Descargar .docx' }}</li>
          <li @click="exportPdf">{{ $t('files.downloadPdf') || 'Descargar .pdf' }}</li>
          <li @click="exportTxt">{{ $t('files.downloadTxt') || 'Descargar .txt' }}</li>
          <li @click="printDoc">{{ $t('files.print') || 'Imprimir' }}</li>
        </ul>
      </div>
      <button
        v-if="!isReadOnly"
        class="ibb-sheet__history"
        @click="showHistory = true"
        :title="$t('files.versionHistory') || 'Historial de versiones'"
      >
        <span class="ibb-sheet__history-icon">⟲</span>
        <span class="ibb-sheet__history-text">{{ $t('files.versionHistory') || 'Historial' }}</span>
      </button>
      <button
        v-if="!isReadOnly"
        class="ibb-sheet__save"
        :disabled="saving || !dirty"
        @click="save"
      >
        {{ $t('buttons.save') || 'Guardar' }}
      </button>
    </div>

    <div ref="mount" class="ibb-sheet__mount ibb-sheet__mount--docs"></div>

    <!-- Barra inferior de estad\u00edsticas del documento (palabras / caracteres / p\u00e1rrafos).
         Se actualiza reactivamente en statsTick. -->
    <div v-if="loaded" class="ibb-sheet__statusbar">
      <span class="ibb-sheet__stat">
        <strong>{{ stats.words }}</strong>
        {{ $t('files.wordsCount') || 'palabras' }}
      </span>
      <span class="ibb-sheet__stat-sep">•</span>
      <span class="ibb-sheet__stat">
        <strong>{{ stats.charsWithSpaces }}</strong>
        {{ $t('files.charsCount') || 'caracteres' }}
      </span>
      <span class="ibb-sheet__stat-sep">•</span>
      <span class="ibb-sheet__stat">
        <strong>{{ stats.paragraphs }}</strong>
        {{ $t('files.paragraphsCount') || 'p\u00e1rrafos' }}
      </span>
    </div>

    <div v-if="!loaded" class="ibb-sheet__loading">
      <div class="ibb-sheet__loading-box">
        <h2>{{ $t('files.loading') || 'Cargando…' }}</h2>
        <p v-if="loadPhase">{{ loadPhase }}</p>
      </div>
    </div>

    <version-history
      v-if="showHistory"
      :node-handle="req && req.handle"
      :parent-handle="req && req.parentHandle"
      :filename="filename"
      :ibb-instance="ibbInstanceForFile"
      @close="showHistory = false"
      @restored="onVersionRestored"
    />

    <div v-if="showUnsavedModal" class="ibb-sheet-modal" @click.self="cancelUnsavedModal">
      <div class="ibb-sheet-modal__card">
        <div class="ibb-sheet-modal__header">
          <h2>{{ $t('prompts.unsavedChangesTitle') || 'Cambios sin guardar' }}</h2>
        </div>
        <div class="ibb-sheet-modal__body">
          {{ $t('prompts.unsavedChanges') || 'Tienes cambios sin guardar. ¿Qué quieres hacer?' }}
        </div>
        <div class="ibb-sheet-modal__footer">
          <button class="ibb-sheet-modal__btn" @click="cancelUnsavedModal">
            {{ $t('buttons.cancel') || 'Cancelar' }}
          </button>
          <button class="ibb-sheet-modal__btn ibb-sheet-modal__btn--save" @click="saveAndLeave">
            {{ $t('prompts.saveAndLeave') || 'Guardar y salir' }}
          </button>
          <button class="ibb-sheet-modal__btn ibb-sheet-modal__btn--discard" @click="discardAndLeave">
            {{ $t('prompts.discardAndLeave') || 'Salir sin guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
 * Editor de documentos basado en Univer Docs (UMD 0.4.2).
 *
 * Estrategia idéntica a SheetEditor.vue:
 *   - Overlay fullscreen (position: fixed, inset: 0).
 *   - Self-host del UMD de Univer (mismo que Sheets).
 *   - Diccionario es-ES propio (utils/univerEsES.js).
 *   - editorSaving en el store para evitar el race de save vs fetchData.
 *   - Modal de cambios sin guardar (sin window.confirm).
 *
 * Persistencia:
 *   Los .docx se guardan con `docxAdapter`. Roundtrip sin pérdida: el snapshot
 *   completo de Univer se embebe en `iberbox/univer-doc.json` dentro del ZIP.
 *   Los .docx generados son válidos en Word (muestran texto plano); cuando
 *   los abre Iberbox, usa el JSON embebido para restaurar formato.
 *
 * Versionado:
 *   Reutiliza el mismo utils/versionHistory.js que Sheets (mismo modal).
 */

import { mapState } from 'vuex';
import { files as api } from '@/api';
import { ROUTES } from '@/router';
import {
  docxBytesToUniverDocData,
  univerDocDataToDocxBytes,
  createEmptyUniverDocData,
  documentStats,
} from '@/utils/docxAdapter';
import { saveVersionSnapshot } from '@/utils/versionHistory';
import univerEsES from '@/utils/univerEsES';
import { wireUniverPanelClose } from '@/utils/univerPanelClose';
import { exportUnitToPdf } from '@/utils/univerPdfExport';
import VersionHistory from '@/components/prompts/VersionHistoryXlsx.vue';

const UNIVER_BASE = '/univer';
const AUTOSAVE_MS = 15000;
const VERSION_SNAPSHOT_MS = 120000;

let univerLoaderPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = false;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('No se pudo cargar ' + src));
    document.head.appendChild(s);
  });
}

function loadCss(href) {
  return new Promise((resolve, reject) => {
    if ([...document.styleSheets].some(s => s.href === href)) return resolve();
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    l.onload = () => resolve();
    l.onerror = () => reject(new Error('No se pudo cargar ' + href));
    document.head.appendChild(l);
  });
}

function loadUniverUMD() {
  if (univerLoaderPromise) return univerLoaderPromise;
  univerLoaderPromise = (async () => {
    await loadCss(UNIVER_BASE + '/univer.css');
    await loadScript(UNIVER_BASE + '/univer.full.umd.js');
    try { await loadScript(UNIVER_BASE + '/locale/en-US.js'); } catch (_) { /* opcional */ }
  })();
  return univerLoaderPromise;
}

export default {
  name: 'docs-editor',
  components: { VersionHistory },
  directives: {
    'click-outside': {
      bind (el, binding) {
        el.__clickOutsideHandler__ = (evt) => {
          if (!(el === evt.target || el.contains(evt.target))) {
            binding.value(evt);
          }
        };
        document.addEventListener('mousedown', el.__clickOutsideHandler__);
      },
      unbind (el) {
        document.removeEventListener('mousedown', el.__clickOutsideHandler__);
        delete el.__clickOutsideHandler__;
      }
    }
  },
  data () {
    return {
      loaded: false,
      saving: false,
      dirty: false,
      isReadOnly: false,
      loadPhase: '',
      univer: null,
      univerAPI: null,
      unitId: null,
      autosaveTimer: null,
      ignoreMutations: true,
      univerInstanceType: null,
      lastSavedAt: null,
      lastVersionAt: null,
      showHistory: false,
      showExportMenu: false,
      showUnsavedModal: false,
      stats: { words: 0, charsWithSpaces: 0, charsNoSpaces: 0, paragraphs: 0 },
      statsTimer: null,
      panelCloseCleanup: null,
    };
  },
  computed: {
    ...mapState(['req', 'permissions', 'permissionsSharedDrive']),
    filename () { return (this.req && this.req.name) || ''; },
    ibbInstanceForFile () {
      return this.$route.name === ROUTES.DRIVE ? (typeof ibbLink !== 'undefined' ? ibbLink : null) : (typeof ibb !== 'undefined' ? ibb : null);
    },
    statusText () {
      if (this.saving) return this.$t('files.saving') || 'Guardando…';
      if (this.dirty) return this.$t('files.unsaved') || 'Cambios sin guardar';
      if (this.lastSavedAt) {
        const hhmm = this.lastSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        return (this.$t('files.savedAt') || 'Guardado a las {time}').replace('{time}', hhmm);
      }
      if (this.loaded) return this.$t('files.saved') || 'Guardado';
      return '';
    },
    statusClass () {
      if (this.saving) return 'ibb-sheet__status--saving';
      if (this.dirty) return 'ibb-sheet__status--dirty';
      if (this.loaded) return 'ibb-sheet__status--ok';
      return '';
    },
    statusIconKind () {
      return this.saving || this.dirty || this.loaded;
    },
  },
  created () {
    window.addEventListener('keydown', this.keyEvent);
    window.addEventListener('beforeunload', this.beforeUnload);
  },
  async mounted () {
    this.isReadOnly = (this.permissions === 1 || this.permissionsSharedDrive === 1);
    try {
      this.loadPhase = 'Cargando motor…';
      await this.$nextTick();
      await loadUniverUMD();

      this.loadPhase = 'Inicializando editor…';
      this.bootUniver();

      this.loadPhase = 'Abriendo archivo…';
      await this.openUnit();

      await this.$nextTick();
      window.dispatchEvent(new Event('resize'));
      setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
      setTimeout(() => { this.ignoreMutations = false; }, 1500);

      // Stats iniciales — ahora s\u00ed hay documento cargado.
      this.scheduleStatsRefresh();

      // Habilitar cerrado de paneles laterales (X + click fuera)
      this.panelCloseCleanup = wireUniverPanelClose(this.$refs.mount);

      this.loaded = true;
    } catch (e) {
      this.$showError(e);
      // eslint-disable-next-line no-console
      console.error('[DocsEditor] fallo:', e);
    }
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.keyEvent);
    window.removeEventListener('beforeunload', this.beforeUnload);
    clearTimeout(this.autosaveTimer);
    clearTimeout(this.statsTimer);
    if (this.panelCloseCleanup) { try { this.panelCloseCleanup(); } catch (_) { /* noop */ } }
    if (this.univer) {
      try { this.univer.dispose(); } catch (_) { /* noop */ }
      this.univer = null;
      this.univerAPI = null;
    }
  },
  methods: {
    beforeUnload (e) {
      if (this.dirty) { e.preventDefault(); e.returnValue = ''; return ''; }
    },
    keyEvent (event) {
      if (!event.ctrlKey && !event.metaKey) return;
      if (String.fromCharCode(event.which).toLowerCase() !== 's') return;
      event.preventDefault();
      if (!this.isReadOnly) this.save();
    },
    goBack () {
      if (this.dirty) { this.showUnsavedModal = true; return; }
      this.doLeave();
    },
    doLeave () {
      this.showUnsavedModal = false;
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ path: '/cloud' });
    },
    cancelUnsavedModal () { this.showUnsavedModal = false; },
    async saveAndLeave () {
      this.showUnsavedModal = false;
      try { await this.save(); } catch (_) { /* save reporta error */ }
      if (!this.dirty) this.doLeave();
    },
    discardAndLeave () {
      this.dirty = false;
      this.doLeave();
    },

    bootUniver () {
      const W = window;
      const Core = W.UniverCore;
      const Design = W.UniverDesign;
      const Facade = W.UniverFacade;
      if (!Core || !Core.Univer || !Design || !Facade) {
        throw new Error('Univer UMD no expuso UniverCore/UniverDesign/UniverFacade.');
      }

      const { Univer, LocaleType, UniverInstanceType, mergeLocales } = Core;
      const { defaultTheme } = Design;
      const { FUniver } = Facade;

      // Diccionario es-ES (mismo que Sheets). Deep-merge con en-US para claves no cubiertas.
      const deepMerge = (target, source) => {
        const out = Array.isArray(target) ? target.slice() : Object.assign({}, target);
        if (!source || typeof source !== 'object') return out;
        for (const k of Object.keys(source)) {
          const sv = source[k];
          const tv = out[k];
          if (sv && typeof sv === 'object' && !Array.isArray(sv)
              && tv && typeof tv === 'object' && !Array.isArray(tv)) {
            out[k] = deepMerge(tv, sv);
          } else {
            out[k] = sv;
          }
        }
        return out;
      };
      const enFull = (W.UniverUMD && W.UniverUMD['en-US']) || {};
      const mergedEs = mergeLocales ? mergeLocales(enFull, univerEsES) : deepMerge(enFull, univerEsES);
      const localeType = LocaleType.EN_US;
      const locales = { [LocaleType.EN_US]: mergedEs };

      const univer = new Univer({ theme: defaultTheme, locale: localeType, locales });

      const reg = (mod, pluginName, config) => {
        if (mod && mod[pluginName]) {
          config === undefined
            ? univer.registerPlugin(mod[pluginName])
            : univer.registerPlugin(mod[pluginName], config);
        }
      };

      // Plugins necesarios para Docs.
      //
      // UniverFormulaEnginePlugin es OBLIGATORIO aunque no usemos f\u00f3rmulas:
      // univer.editor.service depende internamente de LexerTreeBuilder, que
      // registra ese plugin. Sin \u00e9l, createUnit(UNIVER_DOC) lanza
      // "Cannot find LexerTreeBuilder registered by any injector" y el
      // canvas del documento nunca se crea (editor queda en blanco).
      //
      // UniverDocsPlugin con hasScroll:true activa el scroll interno del doc.
      reg(W.UniverEngineRender,  'UniverRenderEnginePlugin');
      reg(W.UniverUi,            'UniverUIPlugin', { container: this.$refs.mount });
      reg(W.UniverEngineFormula, 'UniverFormulaEnginePlugin');
      reg(W.UniverDocs,          'UniverDocsPlugin', { hasScroll: true });
      reg(W.UniverDocsUi,        'UniverDocsUIPlugin');

      this.univer = univer;
      this.univerAPI = FUniver.newAPI(univer);
      this.univerInstanceType = UniverInstanceType;

      try {
        if (this.univerAPI.onCommandExecuted) {
          this.univerAPI.onCommandExecuted((evt) => {
            if (evt && evt.type === 2 /* MUTATION */) {
              this.markDirty();
              this.scheduleStatsRefresh();
            }
          });
        }
      } catch (_) { /* save manual sigue funcionando */ }

      // C\u00e1lculo inicial de stats una vez cargado el doc
      this.scheduleStatsRefresh();
    },

    /**
     * Refresca palabras/caracteres/p\u00e1rrafos con un peque\u00f1o debounce.
     * Se llama desde el listener de mutaciones de Univer.
     */
    scheduleStatsRefresh () {
      clearTimeout(this.statsTimer);
      this.statsTimer = setTimeout(() => {
        try {
          const snap = this.getCurrentSnapshot();
          if (snap) this.stats = documentStats(snap);
        } catch (_) { /* noop */ }
      }, 300);
    },

    /** Toma un snapshot fresco sin serializar a bytes (para stats). */
    getCurrentSnapshot () {
      const doc = this.univerAPI && this.univerAPI.getActiveDocument && this.univerAPI.getActiveDocument();
      if (doc) {
        if (typeof doc.save === 'function') return doc.save();
        if (typeof doc.getSnapshot === 'function') return doc.getSnapshot();
      }
      if (this.univer && typeof this.univer.getSnapshot === 'function' && this.unitId) {
        return this.univer.getSnapshot(this.unitId);
      }
      return null;
    },

    async openUnit () {
      let data;
      if (!this.req || !this.req.handle) {
        data = createEmptyUniverDocData({ id: 'doc-' + Date.now() });
      } else {
        const ibbInstance = this.ibbInstanceForFile;
        const buf = await api.getFileBytes(this.req.handle, ibbInstance);
        data = await docxBytesToUniverDocData(buf, { name: this.req.name });
      }
      const unit = this.univer.createUnit(this.univerInstanceType.UNIVER_DOC, data);
      this.unitId = unit && (unit.getUnitId ? unit.getUnitId() : unit.unitId);
    },

    markDirty () {
      if (this.isReadOnly || this.ignoreMutations) return;
      this.dirty = true;
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = setTimeout(() => {
        if (this.dirty && !this.saving) this.save();
      }, AUTOSAVE_MS);
    },

    async save () {
      if (this.isReadOnly || this.saving) return;
      this.saving = true;
      try { this.$store.commit('setEditorSaving', true); } catch (_) { /* noop */ }
      try {
        const bytes = this.getCurrentBytes();
        const ibbInstance = this.ibbInstanceForFile;

        const self = this;
        const onNewHandle = (newHandle) => {
          if (!newHandle) return;
          try {
            if (self.req) {
              self.$store.commit('updateRequest', { ...self.req, handle: newHandle });
            }
            if (self.$route && self.$route.hash !== '#' + newHandle) {
              self.$router.replace({ path: self.$route.path, hash: '#' + newHandle });
            }
          } catch (_) { /* noop */ }
        };

        await api.putIdBinary(this.req.handle, bytes, ibbInstance, onNewHandle);

        this.dirty = false;
        this.lastSavedAt = new Date();

        const now = Date.now();
        if (!this.lastVersionAt || (now - this.lastVersionAt.getTime()) > VERSION_SNAPSHOT_MS) {
          try {
            await saveVersionSnapshot({
              ibb: ibbInstance,
              parentHandle: this.req && this.req.parentHandle,
              originalName: this.req && this.req.name,
              bytes,
            });
            this.lastVersionAt = new Date();
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[DocsEditor] snapshot de versión falló (no bloqueante):', err && err.message);
          }
        }
      } catch (e) {
        this.$showError(e);
        // eslint-disable-next-line no-console
        console.error('[DocsEditor] error al guardar:', e);
      } finally {
        this.saving = false;
        setTimeout(() => {
          try { this.$store.commit('setEditorSaving', false); } catch (_) { /* noop */ }
        }, 50);
      }
    },

    getCurrentBytes () {
      const snapshot = this.getCurrentSnapshot();
      if (!snapshot) throw new Error('No se pudo obtener el estado del documento');
      return univerDocDataToDocxBytes(snapshot);
    },

    async onVersionRestored (bytes) {
      this.showHistory = false;
      try { this.$store.commit('setEditorSaving', true); } catch (_) { /* noop */ }
      this.saving = true;
      try {
        const ibbInstance = this.ibbInstanceForFile;
        const onNewHandle = (newHandle) => {
          if (!newHandle || !this.req) return;
          try {
            this.$store.commit('updateRequest', { ...this.req, handle: newHandle });
            if (this.$route && this.$route.hash !== '#' + newHandle) {
              this.$router.replace({ path: this.$route.path, hash: '#' + newHandle });
            }
          } catch (_) { /* noop */ }
        };
        await api.putIdBinary(this.req.handle, bytes, ibbInstance, onNewHandle);

        try {
          await saveVersionSnapshot({
            ibb: ibbInstance,
            parentHandle: this.req && this.req.parentHandle,
            originalName: this.req && this.req.name,
            bytes,
          });
          this.lastVersionAt = new Date();
        } catch (err) { /* no bloqueante */ }

        if (this.univer) {
          try { this.univer.dispose(); } catch (_) { /* noop */ }
          this.univer = null;
          this.univerAPI = null;
          this.unitId = null;
        }
        if (this.$refs.mount) this.$refs.mount.innerHTML = '';
        await this.$nextTick();
        this.ignoreMutations = true;
        this.bootUniver();
        await this.openUnit();
        window.dispatchEvent(new Event('resize'));
        setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
        setTimeout(() => { this.ignoreMutations = false; }, 1500);

        this.dirty = false;
        this.lastSavedAt = new Date();
      } catch (e) {
        this.$showError(e);
        // eslint-disable-next-line no-console
        console.error('[DocsEditor] error al restaurar:', e);
      } finally {
        this.saving = false;
        setTimeout(() => {
          try { this.$store.commit('setEditorSaving', false); } catch (_) { /* noop */ }
        }, 50);
      }
    },

    // ---------------------- Exportar / Imprimir ----------------------
    toggleExportMenu () { this.showExportMenu = !this.showExportMenu; },
    closeExportMenu () { this.showExportMenu = false; },
    triggerDownload (bytes, mime, filename) {
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    },
    exportDocx () {
      this.showExportMenu = false;
      try {
        const bytes = this.getCurrentBytes();
        const name = (this.filename && this.filename.toLowerCase().endsWith('.docx'))
          ? this.filename
          : (this.filename || 'documento') + '.docx';
        this.triggerDownload(
          bytes,
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          name
        );
      } catch (e) {
        this.$showError(e);
      }
    },

    /**
     * Export a TXT plano: tomamos el `dataStream` de Univer, reemplazamos
     * separadores internos (\r = fin de p\u00e1rrafo) por salto est\u00e1ndar (\n) y
     * quitamos el cierre (\n final que Univer a\u00f1ade).
     */
    exportTxt () {
      this.showExportMenu = false;
      try {
        const snap = this.getCurrentSnapshot();
        const ds = (snap && snap.body && snap.body.dataStream) || '';
        const body = ds.endsWith('\n') ? ds.slice(0, -1) : ds;
        const plain = body.replace(/\r/g, '\n');
        const baseName = (this.filename || 'documento').replace(/\.[^./]+$/, '');
        this.triggerDownload(
          new TextEncoder().encode('\ufeff' + plain),
          'text/plain;charset=utf-8',
          baseName + '.txt'
        );
      } catch (e) {
        this.$showError(e);
      }
    },

    /**
     * Export a PDF v\u00eda el di\u00e1logo de impresi\u00f3n del navegador.
     *
     * Univer 0.4.2 no expone una API nativa para generar PDF, as\u00ed que:
     *   1. A\u00f1adimos la clase `ibb-sheet-printing` al body (nuestro CSS @media
     *      print oculta topbar, toolbar, statusbar).
     *   2. Forzamos un resize para que Univer repinte el canvas.
     *   3. Llamamos a `window.print()`. El usuario elige "Save as PDF" en el
     *      di\u00e1logo nativo (o imprime directo).
     *   4. Limpiamos la clase al terminar.
     */
    exportPdf () {
      this.showExportMenu = false;
      this.runPrintFlow();
    },
    printDoc () {
      this.showExportMenu = false;
      this.runPrintFlow();
    },

    /**
     * Imprime/exporta a PDF usando la utilidad `univerPdfExport`.
     *
     * Flujo:
     *   1. Expandir el mount para que Univer renderice todo el doc en el
     *      canvas (no s\u00f3lo el viewport).
     *   2. Capturar el canvas a imagen.
     *   3. Partir la imagen en p\u00e1ginas A4.
     *   4. Montar un iframe oculto con esas p\u00e1ginas + page-break.
     *   5. `print()` sobre ese iframe.
     *
     * Esto aisla el PDF de la UI del cliente Iberbox: no usa `@media print`
     * del documento principal, no depende del navegador para paginar, y
     * produce PDFs pixel-perfect con p\u00e1ginas A4 exactas.
     */
    async runPrintFlow () {
      try {
        await exportUnitToPdf({
          mount: this.$refs.mount,
          sheet: this.$el,
          univerAPI: this.univerAPI,
          type: 'doc',
          filename: this.filename || 'documento.pdf',
        });
      } catch (e) {
        this.$showError ? this.$showError(e) : console.error(e);
      }
    },
  },
};
</script>

<style>
/* Los estilos .ibb-sheet* son compartidos con SheetEditor; los importamos
 * desde un CSS standalone para que cada chunk lazy-loaded tenga las reglas
 * aunque el otro editor no se haya cargado nunca en la sesión.
 * No usamos `scoped` porque forzamos estilos sobre la DOM de Univer. */
@import "../../styles/ibb-editor.css";

.ibb-sheet__mount--docs {
  /* Fondo gris tipo Word online; Univer Docs dibuja la p\u00e1gina blanca encima. */
  background: #f5f5f7;
}

/* ------ Barra inferior de estad\u00edsticas ------ */
.ibb-sheet__statusbar {
  flex: 0 0 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  background: #f7f7f9;
  border-top: 1px solid #e4e4e8;
  font-size: 12px;
  color: #4b5563;
  user-select: none;
}
.ibb-sheet__stat strong {
  color: #111827;
  font-weight: 600;
  margin-right: 4px;
}
.ibb-sheet__stat-sep {
  color: #d4d4d8;
}
@media print {
  body.ibb-sheet-printing .ibb-sheet__statusbar {
    display: none !important;
  }
}
</style>
